// vueui/src/stores/notifications.ts

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getToken, handleGlobalAuthError, getImgVersion } from '@/assets/common'
import { API_ENDPOINTS, getApiUrl, apiRequest } from '@/assets/api'
import { useAuthStore } from './auth'
import { useAdminSettingsStore } from './adminSettings'

// Constants
const DEFAULT_POLL_INTERVAL_MS = 5000 // 5 seconds (faster webhook detection)

export interface Notification {
  id: string
  type: 'webhook_received' | 'files_downloading' | 'pdf_job_queued' | 'pdf_processing' | 'pdf_uploading' | 'pdf_job_completed' | 'pdf_job_failed'
  title: string
  message: string
  timestamp: string
  jobId?: string
  repoId?: string
  chapter?: string
  commitSha?: string
  read: string // Changed to string to match DynamoDB schema
  data?: any
}

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])
  const isConnected = ref(false)
  const lastPollTime = ref<number>(0)
  const pollInterval = ref<NodeJS.Timeout | null>(null)
  
  // Get admin settings store for dynamic polling interval
  const adminSettingsStore = useAdminSettingsStore()

  // Callback system for webhook notifications
  const webhookNotificationCallbacks = ref<Array<(notification: Notification) => void>>([])
  
  const onWebhookNotification = (callback: (notification: Notification) => void) => {
    webhookNotificationCallbacks.value.push(callback)
  }
  
  const removeWebhookNotificationCallback = (callback: (notification: Notification) => void) => {
    const index = webhookNotificationCallbacks.value.indexOf(callback)
    if (index > -1) {
      webhookNotificationCallbacks.value.splice(index, 1)
    }
  }

  // Computed properties
  const unreadCount = computed(() => notifications.value.filter(n => n.read !== 'true').length)
  const recentNotifications = computed(() => 
    notifications.value
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10)
  )

  // Actions
  const addNotification = async (notification: Omit<Notification, 'id' | 'read'>) => {
    // Check if this notification already exists (based on multiple criteria)
    const existingNotification = notifications.value.find(n => {
      // For PDF events, check jobId first (most unique)
      if (notification.jobId && n.jobId === notification.jobId) {
        return true
      }
      // For webhook events, check timestamp and type
      if (notification.type === 'webhook_received' && 
          n.type === 'webhook_received' && 
          n.timestamp === notification.timestamp) {
        return true
      }
      // Fallback: check timestamp, title, and message
      return n.timestamp === notification.timestamp && 
             n.title === notification.title &&
             n.message === notification.message
    })
    
    if (existingNotification) {
      console.log('Skipping duplicate notification:', notification.title, notification.jobId || '')
      return
    }
    
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      read: 'false'
    }
    
    notifications.value.unshift(newNotification)
    
    // Keep only last 50 notifications
    if (notifications.value.length > 50) {
      notifications.value = notifications.value.slice(0, 50)
    }

    // Show browser notification if supported
    if ('Notification' in window && Notification.permission === 'granted') {
      const v = await getImgVersion()
      new Notification(notification.title, {
        body: notification.message,
        icon: `/favicon.svg?v=${v}`,
        tag: newNotification.id
      })
    }
  }

  const markAsRead = (id: string) => {
    const notification = notifications.value.find(n => n.id === id)
    if (notification) {
      notification.read = 'true'
      // TODO: Create individual notification marking endpoint for proper persistence
    }
  }

  const markAllAsRead = async () => {
    try {
      console.log('📝 Marking all notifications as read')
      
      // Call the backend to mark all notifications as read
      const url = getApiUrl(API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS + '?markAsRead=true')
      const response = await apiRequest(url)
      
      if (response.ok) {
        const data = await response.json()
        console.log('✅ All notifications marked as read in database')
        
        // Update local state with the response from server
        notifications.value = data.notifications || []
      } else {
        console.error('❌ Failed to mark notifications as read:', response.statusText)
        // Fallback to local update if backend call fails
        notifications.value.forEach(n => n.read = 'true')
      }
    } catch (error) {
      console.error('❌ Error marking notifications as read:', error)
      // Fallback to local update if there's an error
      notifications.value.forEach(n => n.read = 'true')
    }
  }

  const clearNotifications = async () => {
    try {
      const url = getApiUrl(API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS + '?clearAll=true')
      console.log('🗑️ Clearing all notifications, URL:', url)
      
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)
      
      const response = await apiRequest(url, {
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)

      if (response.ok) {
        const data = await response.json()
        console.log('🗑️ Cleared all notifications')
        notifications.value = data.notifications || []
      } else {
        console.error('Failed to clear notifications:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error clearing notifications:', error)
    }
  }

  const removeNotification = (id: string) => {
    notifications.value = notifications.value.filter(n => n.id !== id)
  }

  // Fetch notifications from the new EventBridge-driven system
  const fetchNotifications = async (markAsRead: boolean = false) => {
    try {
      const url = getApiUrl(API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS + (markAsRead ? '?markAsRead=true' : ''))
      console.log('🔔 MSTRTZT fN 00 tstamp=${new Date().toISOString()} Fetching notifications from EventBridge system, URL:', url)
      
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)

      const response = await apiRequest(url, {
        signal: controller.signal
      })

      console.log(`MSTRTZT fN 01 tstamp=${new Date().toISOString()}; called /notifications endpoint`)
      
      clearTimeout(timeoutId)

      if (response.ok) {
        const data = await response.json()
        // console.log(`MSTRTZT fN 02 tstamp=${new Date().toISOString()}; data=${JSON.stringify(data)}`)
        console.log('🔔 Received notifications:', data.notifications?.length || 0, 'notifications')
        
        // Check for new webhook notifications and trigger callbacks
        const previousNotifications = notifications.value
        const newNotifications = data.notifications || []
        
        // Find new webhook notifications
        const newWebhookNotifications = newNotifications.filter((newNotif: Notification) => {
          // Check if this is a new webhook notification
          const isWebhookNotification = newNotif.title === 'Webhook Job Starting' || 
                                      newNotif.title === 'Manual Job Starting'
          
          if (!isWebhookNotification) return false
          
          // Check if it's actually new (not in previous notifications)
          const isNew = !previousNotifications.some(prevNotif => 
            prevNotif.id === newNotif.id || 
            (prevNotif.timestamp === newNotif.timestamp && prevNotif.title === newNotif.title)
          )
          
          return isNew
        })
        
        // Trigger callbacks for new webhook notifications
        if (newWebhookNotifications.length > 0) {
          console.log(`🔄 Found ${newWebhookNotifications.length} new webhook notifications, triggering callbacks`)
          newWebhookNotifications.forEach((notification: Notification) => {
            webhookNotificationCallbacks.value.forEach(callback => {
              try {
                callback(notification)
              } catch (error) {
                console.error('Error in webhook notification callback:', error)
              }
            })
          })
        }
        
        // Replace all notifications with the new data
        notifications.value = newNotifications
        isConnected.value = true
      } else if (response.status === 401) {
        console.log(`MSTRTZT fN 03 tstamp=${new Date().toISOString()}`)
        // Handle authentication error - session expired
        console.warn('🔔 Authentication expired during notification fetch')
        isConnected.value = false
        stopPolling()
        
        // Use global auth error handler
        handleGlobalAuthError('Your session has expired. Please sign in again to continue receiving notifications.')
      } else {
        isConnected.value = false
      }
    } catch (error) {
      console.warn('Notification fetch failed:', error)
      isConnected.value = false
    }
  }

  // Poll for new notifications (now using the new API)
  const startPolling = () => {
    if (pollInterval.value) return // Already polling
    
    const poll = async () => {
      await fetchNotifications()
    }

    // Poll immediately, then at the configured interval
    poll()
    const pollIntervalMs = adminSettingsStore.getSetting('notificationPollingInterval') || DEFAULT_POLL_INTERVAL_MS
    pollInterval.value = setInterval(poll, pollIntervalMs)
  }

  const stopPolling = () => {
    if (pollInterval.value) {
      clearInterval(pollInterval.value)
      pollInterval.value = null
    }
    isConnected.value = false
  }

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }
    return Notification.permission === 'granted'
  }

  return {
    // State
    notifications,
    isConnected,
    lastPollTime,
    
    // Computed
    unreadCount,
    recentNotifications,
    
    // Actions
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    removeNotification,
    fetchNotifications,
    startPolling,
    stopPolling,
    requestNotificationPermission,
    
    // Webhook notification callbacks
    onWebhookNotification,
    removeWebhookNotificationCallback
  }
})
